/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useEffect } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router';
import { Box, withAuthRedirect, DataState, DataStateError, CardListSkeleton } from '@/shared/ui';
import { BuildingIcon } from '@/shared/ui/components/basic/icons';
import { useTranslation } from 'react-i18next';
import { PageSEO } from '@/shared/seo';
import { cn, tailwindUtils } from '@/shared/ui/utils/tailwind';

import type { AgencyDashboardItem } from './ManageAgenciesCards';
import { WorkspaceSelector } from './WorkspaceSelector';
import { useAgenciesData } from './use-agencies-data';
import { OnboardingPage } from '@/features/onboarding';
import { prefetchPlans } from '@/features/onboarding/hooks/plans-cache';

/**
 * Workspace/agency management page. Loads the user's agencies and either renders
 * the onboarding flow (when adding an agency or when the user has none) or the
 * workspace selector grid, driving both via the `action`/`step` URL params.
 *
 * @returns The workspace management screen.
 */
const ManageAgencies = () => {
  const { t } = useTranslation('@pages/agencies');
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { agencies, hasLoaded, loadError, retry } = useAgenciesData();
  const hasAgencies = agencies.length > 0;

  // The plan step is now the first thing the onboarding shows, so the
  // catalogue fetch used to start only once PlanChoice mounted — after the
  // lazy chunk had loaded, in series with it. The step the prefetch used to
  // hang off (SoloBrokerData/AgencyData) no longer sits before the plan, so
  // nothing was warming the cache at all. Firing it here puts the request in
  // flight alongside the agencies load instead of behind it; `loadPlans`
  // dedupes, so arriving on the step costs nothing more.
  useEffect(() => { prefetchPlans(); }, []);

  const stepParam = searchParams.get('step');
  const isAddingAgency = searchParams.get('action') === 'add' || stepParam !== null;
  const isWorkspaceRoute = location.pathname === '/app/workspace' || location.pathname === '/app/workspace/';

  useEffect(() => {
    if (isAddingAgency) return;
    if (hasLoaded && hasAgencies && stepParam === 'action' && searchParams.get('action') !== 'add') {
      setSearchParams({}, { replace: true });
    }
  }, [hasLoaded, hasAgencies, stepParam, searchParams, setSearchParams, isAddingAgency]);

  useEffect(() => {
    if (isAddingAgency) return;
    if (hasLoaded && !loadError && !hasAgencies && isWorkspaceRoute) {
      navigate('/app/agencies', { replace: true });
    }
  }, [hasLoaded, loadError, hasAgencies, isWorkspaceRoute, navigate, isAddingAgency]);

  if (isAddingAgency) {
    return (
      <Box
        className={cn(tailwindUtils.fullWidthContained, 'min-h-full flex flex-col')}
        style={{
          overflowX: 'hidden',
          background: 'var(--surface-secondary)',
        }}
      >
        <PageSEO title={t('pageTitle')} />
        <OnboardingPage />
      </Box>
    );
  }

  if (!hasLoaded) {
    return (
      <Box
        className={cn(tailwindUtils.fullWidthContained, 'h-full')}
        style={{
          marginTop: '-1rem',
          background: 'var(--surface-secondary)',
        }}
      >
        <PageSEO title={t('pageTitle')} />
        <DataState
          isLoading
          isEmpty={false}
          loadingFallback={
            <div className="w-full max-w-5xl mx-auto p-(--space-10)">
              <CardListSkeleton withAvatar count={3} />
            </div>
          }
        >
          {null}
        </DataState>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        className={cn(tailwindUtils.fullWidthContained, 'h-full flex flex-col')}
        style={{
          marginTop: '-1rem',
          background: 'var(--surface-secondary)',
        }}
      >
        <PageSEO title={t('pageTitle')} />
        <DataState
          isLoading={false}
          isError
          isEmpty={false}
          onRetry={retry}
          errorMessage={t('workspaceLoadError.message')}
          errorState={
            <DataStateError
              icon={<BuildingIcon width={28} height={28} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />}
              message={t('workspaceLoadError.message')}
              retryLabel={t('workspaceLoadError.retry')}
              onRetry={retry}
            />
          }
        >
          {null}
        </DataState>
      </Box>
    );
  }

  if (hasLoaded && !hasAgencies) {
    return (
      <Box
        className={cn(tailwindUtils.fullWidthContained, 'min-h-full flex flex-col')}
        style={{
          overflowX: 'hidden',
          background: 'var(--surface-secondary)',
        }}
      >
        <PageSEO title={t('pageTitle')} />
        <OnboardingPage />
      </Box>
    );
  }

  return (
    <Box
      className={cn(tailwindUtils.fullWidthContained, 'min-h-full flex flex-col')}
      style={{
        overflowX: 'hidden',
        background: 'var(--surface-secondary)',
      }}
    >
      <PageSEO title={t('pageTitle')} />
      <WorkspaceSelector
        agencies={agencies as AgencyDashboardItem[]}
        onAgencyRemoved={retry}
      />
    </Box>
  );
};

export default withAuthRedirect(ManageAgencies);
